import { z } from 'zod';
// Shared import/export envelopes (ADR-041).
//
// The column-model = import template = export column set is ONE artifact. A
// feature declares its column-model instance in the backend (bound to the
// entity's existing Zod schema per ADR-009 + an upsert adapter); the engine
// exposes a serializable DESCRIPTOR of it here for the frontend (the export
// preset picker + the template metadata). Per-row validation reuses the
// entity's existing schema — these envelopes are the only NEW Zod the pattern
// adds. The full backend column-model (Zod schema + adapters) is not
// serializable and lives in `core/import-export/types.ts`.
// Typed-column vocabulary — drives the Excel numFmt + value coercion + the
// header-unit check (mirrors ADR-030's canonical-number contract: export writes
// the raw number + a format mask, never pre-formatted text).
export const ColumnTypeSchema = z.enum([
    // 'secret' — a write-only cell (a password, a community): parsed as text,
    // sealed by the consumer, and NEVER echoed anywhere: not in preview values,
    // error messages, run rows or logs; exported BLANK (structure, never values).
    'secret',
    'text',
    'integer',
    'number',
    'percent',
    'duration',
    'date',
    'datetime',
    'boolean',
    'coordinate',
    // An ORDERED list of references in one cell, '>'-delimited (e.g. an assignment
    // chain's assignees `alice > bob`, lead→backup). Parses to a trimmed string[]
    // preserving order; the column-model adapter resolves each token via its
    // `reference` set (username → id), rejecting unknowns with `fk_not_found`.
    // The bulk-`assign` op ([ADR-047](../decisions/ADR-047.md)) is the first consumer.
    'ref_list',
]);
export const ImportFormatSchema = z.enum(['xlsx', 'csv']);
// Import operations differ by what a row IS ([ADR-047](../decisions/ADR-047.md)):
//   • 'create_update' — the row is a scalar entity, upserted on its match key.
//   • 'assign'        — the row is an ordered accountability chain identified by
//                       (scope × responsibility); its payload is the ordered
//                       assignee list. Delegates to core/accountability.
//   • 'change'        — the DATED-CHANGE op for temporal (SCD) data: the row is
//                       "these values, as of `effective_from`". No timeline →
//                       first period (no date → today); date > current start →
//                       a recorded change; date = current start, or no date on
//                       a configured entity → a correction; date < current
//                       start → rejected (history rewrites are UI surgery).
//                       No value columns at all → a no-op skip. First consumer:
//                       site solution configs (ADR-059).
//   • 'transition'    — the LIFECYCLE op: the row is a state-machine step on an
//                       EXISTING entity (`reference · action [· reason · the
//                       declared date of the one step that has one]`); it
//                       creates nothing and corrects no data. The preview runs the
//                       per-row gates the UI verb runs (completeness · seats ·
//                       reach · the machine); a row already at its target
//                       status is a skip. First consumer: site lifecycle
//                       (activate · suspend · resume; decommission stays a UI
//                       act — terminal).
// The 'associate' op (upsert global entity + ensure M:N link) is a separate
// family (APP-179), not yet added.
export const ImportOperationSchema = z.enum(['create_update', 'assign', 'change', 'transition']);
// Serializable projection of one column-model column. The frontend renders the
// export preset picker + reads template metadata from this.
export const ColumnDescriptorSchema = z
    .object({
    key: z.string().min(1).max(64),
    headerKey: z.string().min(1).max(128), // i18n key resolved per language (EN/FR/AR)
    type: ColumnTypeSchema,
    unit: z.string().max(64).optional(), // canonical unit annotation (kW, h, …) — on a datetime column, the export's IANA zone (ADR-082 §8); checked against the file header
    group: z.string().max(64).optional(), // preset / picker section
    required: z.boolean().optional(),
    isMatchKey: z.boolean().optional(), // the single natural-key column used for upsert
    enumValues: z.array(z.string()).optional(), // STATIC valid-value set, listed on the info sheet's column legend
    // DYNAMIC valid-value set (ADR-045): the column's values are one of a
    // reference set resolved per-request from project data (e.g. 'roles' → the
    // project's role names; later 'zones' / 'site_types'). Names a key the caller
    // injects into the build's `references` map; the engine lists the resolved
    // values in the legend (and, when import lands, validates against them). Use
    // this — not `enumValues` — whenever the set is per-project, not fixed in code.
    reference: z.string().max(64).optional(),
    // Export-only / derived column (e.g. a derived status, last-login, an MFA
    // flag). Written to the export for human reading, OMITTED from the blank
    // template, and IGNORED on import — its value is never mapped, validated, or
    // upserted (a register file never flips a lifecycle status — that is the
    // `transition` op's own dataset, ADR-041 / ADR-045). The legend marks it
    // informational. A match key must never be readOnly.
    readOnly: z.boolean().optional(),
    // Recognized on import but OMITTED from the blank template and the export
    // (a rarely-filled sibling of a visible column — the Devices sheet's SIM
    // slots 3–4 beside the two the template shows). The legend does not list it.
    templateHidden: z.boolean().optional(),
    // Header accent tier (ADR-045 §5). Editable columns paint the FULL brand
    // primary; 'soft' paints the light tint instead — a deliberate visual
    // sub-tier for a column group inside one editable set. The ONLY consumer is
    // the contract-configurations model (commitment columns vs pricing axes);
    // everywhere else editable = full primary, readOnly = grey.
    accent: z.enum(['soft']).optional(),
    // Applicability caveat for the legend's "On import" cell, as a label-map
    // KEY resolved per language (e.g. "aggregate categories only"). For a
    // column that only applies to a subset of a dataset's rows — first
    // consumer: the assets sheet's unit_count / device columns.
    noteKey: z.string().max(128).optional(),
})
    .strict();
export const ColumnModelDescriptorSchema = z
    .object({
    entityType: z.string().min(1).max(64),
    matchKey: z.string().min(1).max(64),
    columns: z.array(ColumnDescriptorSchema),
})
    .strict();
// One per-row failure — the unit the errors-only view + the downloadable error
// file render from. `column` is null for row-level failures (duplicate match
// key, unmapped/missing required column).
export const ImportRowErrorSchema = z
    .object({
    row: z.number().int().nonnegative(), // SPREADSHEET row (header = 1, first data row = 2 — what Excel shows); 0 = file-level
    column: z.string().max(64).nullable(),
    code: z.string().max(64), // machine reason: 'required' | 'invalid' | 'unit_mismatch' | 'duplicate_match_key' | 'fk_not_found' | …
    message: z.string().max(500),
})
    .strict();
// A non-blocking summary surfaced in the preview before the all-or-nothing
// commit — the generic channel for "this import will also do / NOT do
// something" callouts (ADR-047). `code` is the machine reason the consumer maps
// to localized copy; `count` is the affected cardinality; `column` narrows a
// per-column warning. Consumers: the `assign` op's `{ code: 'dissociation' }`
// (chain members a row drops) and the engine's `{ code: 'readonly_changed',
// column }` (a read-only column — e.g. a lifecycle `status` — differs from the
// current data in N rows; import will NOT apply it, the user is told where the
// right surface is).
export const ImportWarningSchema = z
    .object({
    code: z.string().max(64), // 'dissociation' | 'readonly_changed' | …
    count: z.number().int().nonnegative(),
    column: z.string().max(64).optional(),
})
    .strict();
// A cell value as it rides the preview diff — the canonical raw value (codes,
// not localized labels; ref_list = the ordered token list).
const CellValueSchema = z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]);
// How each file header mapped — the preview's column honesty block. `unknown`
// headers are ignored silently by the pipeline (a foreign file may carry extra
// columns by design), so the preview must SAY so — a typo'd optional column is
// otherwise silent data loss (the user thinks it imported).
export const ImportColumnMappingSchema = z
    .object({
    header: z.string().max(255), // as written in the file
    key: z.string().max(64).nullable(), // the mapped column key; null = unknown
    // 'forbidden' = a header of a column WITHHELD from this caller by a gate
    // (e.g. a books column without asset.books:read) — the file fails at the
    // header row (`column_forbidden`), never silently "unknown".
    status: z.enum(['mapped', 'readonly_ignored', 'unknown', 'forbidden']),
})
    .strict();
// One field an update row changes: canonical old → new.
export const ImportRowChangeSchema = z
    .object({
    column: z.string().max(64),
    from: CellValueSchema.optional(),
    to: CellValueSchema.optional(),
})
    .strict();
// One classified (non-error) row of the dry-run — the preview outcome browser's
// unit. `verb` is the dataset-specific refinement of `outcome` (the dated-change
// op says 'first_period' / 'change' / 'correction'); absent → the outcome IS the
// verb. `changes` rides update rows (changed fields, old → new).
export const ImportPreviewRowSchema = z
    .object({
    row: z.number().int().positive(), // SPREADSHEET row (header = 1, first data row = 2)
    outcome: z.enum(['create', 'update', 'skip']),
    matchKeyValue: z.string().max(255),
    verb: z.string().max(32).optional(),
    changes: z.array(ImportRowChangeSchema).optional(),
})
    .strict();
// Vocabulary provisioning (ADR-059 §7): one provisionable column's to-be-created
// values — distinct (CI-deduped), each with the number of file rows referencing
// it. The preview's enumerated consent block; the row counts are the typo
// detector ('Riyad' (1 row) beside 2,900 matched 'Riyadh').
export const ImportRefCreateSchema = z
    .object({
    column: z.string().max(64),
    values: z.array(z.object({ value: z.string().max(255), rows: z.number().int().nonnegative() }).strict()),
})
    .strict();
// Reference PICKING — the sibling of provisioning for reference columns whose
// unknown values are not creatable-by-name but MAPPABLE by a human (first
// consumer: the assets `model` column — a customer product code maps to an
// existing catalog row; creating a local model from the raw code would seed
// duplicates the merge loop then has to unwind). The preview enumerates each
// distinct unmatched value (with in-memory suggestions); the wizard collects
// the user's picks; the mapping rides the run's query params back into
// re-preview/commit, re-validated server-side. Unpicked values remain row
// errors, so the all-or-nothing gate needs no extra consent flag.
export const ImportRefPickSchema = z
    .object({
    column: z.string().max(64),
    values: z.array(z
        .object({
        value: z.string().max(255),
        rows: z.number().int().nonnegative(),
        suggestions: z
            .array(z.object({ id: z.string().max(16), label: z.string().max(255) }).strict())
            .max(8),
    })
        .strict()),
})
    .strict();
// Preview = dry-run result (zero writes). The commit is gated on this being
// clean (all-or-nothing) for reference/master data. Beyond the counts + the
// errors list, the preview is outcome-TRANSPARENT: `columns` says how every
// file header mapped (incl. ignored read-only + unknown ones), `rows` lists
// each classified row with its dataset verb + changed fields (capped at
// PREVIEW_ROWS_CAP; `rowsTruncated` says so), and `warnings` carries the
// non-blocking callouts (dissociations, read-only columns that differ) the
// wizard surfaces before commit. `provisionable` = this dataset supports
// vocabulary provisioning AND the actor holds its gate (the wizard's offer
// signal); `refCreates` enumerates what a provision-on run will create.
export const PREVIEW_ROWS_CAP = 1_000;
// The file limits — ONE home for the server's refusals and the words the export
// dialog and the import wizard say about them. The Excel pair exists because the
// Excel codec builds a whole sheet in memory on ONE server thread: reading costs
// about 90 bytes of memory per byte of sheet XML, writing freezes every other
// request about 0.04 ms a row — measured, the budgets keep the largest real
// datasets on Excel (8,803 battery strings: a 4.9 MB sheet) and send larger
// ones to CSV, which has no such cost. The codec change that lifts them is
// APP-180's.
/** Rows one import file may carry (every dataset). */
export const IMPORT_MAX_ROWS = 20_000;
/** Bytes one uploaded import file may weigh (every import route). */
export const IMPORT_FILE_MAX_BYTES = 10 * 1024 * 1024;
/** Rows an Excel export may carry — a bigger view exports as CSV. */
export const EXCEL_EXPORT_MAX_ROWS = 20_000;
/** The largest data sheet (its XML + the shared text, unpacked) an Excel import may open. */
export const EXCEL_IMPORT_MAX_SHEET_BYTES = 8 * 1024 * 1024;
/** The refusal codes of those limits (an import FILE error's `code`, or an export's error-envelope code). */
export const FILE_LIMIT_CODES = {
    tooManyRows: 'too_many_rows',
    excelTooLarge: 'excel_too_large',
    uploadTooLarge: 'PAYLOAD_TOO_LARGE',
    excelExportTooLarge: 'EXCEL_EXPORT_TOO_LARGE',
    /** A view past ONE cap for both formats (the audit trail): the person narrows the view, no CSV fallback. */
    exportTooLarge: 'EXPORT_TOO_LARGE',
};
export const ImportPreviewSchema = z
    .object({
    total: z.number().int().nonnegative(),
    createCount: z.number().int().nonnegative(),
    updateCount: z.number().int().nonnegative(),
    skipCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    errors: z.array(ImportRowErrorSchema),
    warnings: z.array(ImportWarningSchema).optional(),
    columns: z.array(ImportColumnMappingSchema),
    rows: z.array(ImportPreviewRowSchema),
    rowsTruncated: z.boolean().optional(),
    provisionable: z.boolean().optional(),
    refCreates: z.array(ImportRefCreateSchema).optional(),
    refPicks: z.array(ImportRefPickSchema).optional(),
})
    .strict();
// Commit result — the durable run summary (mirrors the `import_jobs` row).
export const ImportResultSchema = z
    .object({
    jobId: z.string().max(16),
    status: z.enum(['committed', 'failed']),
    total: z.number().int().nonnegative(),
    createCount: z.number().int().nonnegative(),
    updateCount: z.number().int().nonnegative(),
    skipCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    errors: z.array(ImportRowErrorSchema),
    warnings: z.array(ImportWarningSchema).optional(),
})
    .strict();
